// ANT Modules
import { Form, FormInstance, Input, Select } from "antd";

// Components
import Label from "components/atoms/Label/Label";
import ErrorMessage from "components/molecules/ErrorMessage/ErrorMessage";
import DateTimePicker from "components/atoms/DateTimePicker/DateTimePicker";
import InputPrefix from "components/atoms/InputPrefix/InputPrefix";
import OutletOption from "components/atoms/OutletOption/OutletOption";

// Styles
import styles from "components/organisms/CreateClaimDetails/CreateClaimDetails.module.css";

// Type Definitions

import type { ClaimDetails, ClaimFieldErrors } from "containers/CreateRetrospectiveClaim/useCreateRetrospectiveClaim";

// Constants & Utils
import { CREATE_RETRO_CLAIM_FORM as Constants } from "constants/global";
import getIcon from "utils/getIcon";
import { OutletItem } from "services/api/types";

const { Option } = Select;

type UserInfoType = {
	username?: string;
	user_id?: string;
};

export type CreateClaimDetailsProps = {
	claimDetails: ClaimDetails;
	outletCode: string;
	terminal: string;
	terminalsList: Array<{ id: string; value: string }>;
	filteredOutletsList: Array<OutletItem>;
	form?: FormInstance<any>;
	handleTerminalSelect: (v: string) => void;
	handleInputChange: (e: any) => void;
	handleOutletSearch: (value: string) => void;
	handleOutletSelect: (value: string) => void;
	validateFields: (arg: boolean) => void;
	fieldErrors?: ClaimFieldErrors;
	userInfo?: UserInfoType;
	handleDateChange?: (date: any, dateString: string) => void;
	handleDateOk?: (value: moment.Moment) => void;
	outletsList?: Array<OutletItem>;
};

// Applying styles based on fieldErrors
const fieldStyle = (isErrorPresent: boolean) => {
	return isErrorPresent ? styles?.err : styles?.filled;
};

const CreateClaimDetails = ({
	claimDetails,
	outletCode,
	userInfo,
	filteredOutletsList,
	terminalsList,
	terminal,
	form,
	handleTerminalSelect,
	validateFields,
	fieldErrors,
	handleInputChange,
	handleOutletSearch,
	handleOutletSelect,
	handleDateChange,
	handleDateOk
}: CreateClaimDetailsProps): JSX.Element => {
	return (
		<Form form={form} className={styles?.form} requiredMark={false} layout="vertical">
			<div className="create-claim-form">
				<div className="md:flex">
					<Form.Item name="username" initialValue={userInfo?.username} label={<Label label={Constants.USERNAME} classes={{ label: styles?.basic_input_label }} />}>
						<Input disabled data-testid={Constants.USERNAME} placeholder={Constants.USERNAME_PLACEHOLDER} className={`${styles?.basic_input_small}`} />
					</Form.Item>
					<Form.Item name="agent_id" initialValue={userInfo?.user_id} label={<Label label={Constants.AGENT_ID} classes={{ label: styles?.basic_input_label }} />}>
						<Input disabled placeholder={Constants.AGENT_ID_PLACEHOLDER} className={`${styles?.basic_input_small}`} />
					</Form.Item>
				</div>
				<div className="md:flex">
					<Form.Item
						extra={fieldErrors?.receipt_number && <ErrorMessage classes={styles?.err_message} message={Constants.ERROR_MESSAGE.RECEIPT_NUMBER} />}
						name="receipt_number"
						initialValue={claimDetails?.receipt_number}
						label={<Label label={Constants.RECEIPT_NUMBER} classes={{ label: styles?.basic_input_label }} requiredMark />}
					>
						<Input
							data-testid={Constants.RECEIPT_NUMBER}
							placeholder={Constants.RECEIPT_NUMBER_PLACEHOLDER}
							className={`${styles?.basic_input_small} ${claimDetails?.receipt_number ? fieldStyle(!!fieldErrors?.receipt_number) : ""}`}
							value={claimDetails?.receipt_number}
							onChange={handleInputChange}
							onBlur={() => {
								validateFields(false);
							}}
						/>
					</Form.Item>
					<Form.Item name="terminal" label={<Label label={Constants.TERMINAL} classes={{ label: styles?.basic_input_label }} requiredMark />}>
						<Select
							data-testid={Constants.TERMINAL}
							showSearch
							className={`outlet-select ${styles?.basic_input_select} ${terminal ? styles?.filled : ""} text-clip`}
							placeholder={Constants.TERMINAL_PLACEHOLDER}
							optionFilterProp="children"
							onChange={handleTerminalSelect}
							value={terminal}
							listHeight={325}
							style={{ width: "100%", minWidth: "12.5rem" }}
							popupClassName="left-nav-search-dropdown"
							dropdownStyle={{ backgroundColor: "#FAFBFC", boxShadow: "none" }}
							suffixIcon={<span>{getIcon({ icon: "" })}</span>}
							filterOption={(input, option) => (option?.value as unknown as string).toLowerCase().includes(input.toLowerCase())}
						>
							{terminalsList?.map((terminalsListVal: { id: string; value: string }) => {
								return (
									<Option value={`${terminalsListVal?.value}`} key={terminalsListVal.id} className="flex-col px-2 py-2">
										{`${terminalsListVal?.value}`}
									</Option>
								);
							})}
						</Select>
					</Form.Item>
				</div>
				<Form.Item name="outlet_code" label={<Label label={Constants.OUTLET} classes={{ label: styles?.basic_input_label }} requiredMark />}>
					<Select
						id="outlet_code"
						disabled={!terminal}
						data-testid={Constants.OUTLET}
						showSearch
						value={outletCode}
						className={`crsc_outlet_select ${styles?.outlet_selection} ${terminal && outletCode ? styles?.filled : ""}`}
						placeholder={Constants.OUTLET_PLACEHOLDER}
						optionFilterProp="children"
						onChange={handleOutletSelect}
						onSearch={handleOutletSearch}
						listHeight={325}
						style={{ width: "100%", minWidth: "12.5rem" }}
						popupClassName="left-nav-search-dropdown"
						dropdownStyle={{ backgroundColor: "#FAFBFC", boxShadow: "none" }}
						suffixIcon={<></>}
						filterOption={(input, option) => (option?.value as unknown as string).toLowerCase().includes(input.toLowerCase())}
					>
						{filteredOutletsList?.map((outlet: OutletItem) => {
							return (
								<Option
									value={`${outlet.outlet_name}*${outlet.outlet_code}*${outlet.terminal}*${outlet?.location || ""}`}
									key={`${outlet.outlet_name}*${outlet.outlet_code}`}
									className="flex-col px-2 py-2"
								>
									<OutletOption outlet_name={outlet.outlet_name} outlet_code={outlet.outlet_code} terminal={outlet?.terminal} location={outlet?.location} />
								</Option>
							);
						})}
					</Select>
				</Form.Item>
				<Form.Item
					extra={fieldErrors?.cr_card_number && <ErrorMessage classes={styles?.err_message} message={Constants.ERROR_MESSAGE.CARD_NUMBER} />}
					name="cr_card_number"
					initialValue={claimDetails?.cr_card_number}
					label={<Label label={Constants.CARD_NUMBER} classes={{ label: styles?.basic_input_label }} requiredMark />}
				>
					<Input
						data-testid={Constants.CARD_NUMBER}
						placeholder={Constants.CARD_NUMBER_PLACEHOLDER}
						className={`${styles?.basic_input_large} ${claimDetails?.cr_card_number ? fieldStyle(!!fieldErrors?.cr_card_number) : ""}`}
						value={claimDetails?.cr_card_number}
						onChange={handleInputChange}
						onBlur={() => {
							validateFields(false);
						}}
						pattern="[0-9]"
						maxLength={19}
					/>
				</Form.Item>
				<Form.Item name="receipt_datetime" label={<Label label={Constants.RECEIPT_DATE_TIME} classes={{ label: styles?.basic_input_label }} requiredMark />}>
					<DateTimePicker
						classes={`date_style ${styles?.basic_input_large} ${claimDetails?.receipt_datetime ? styles?.filled : ""}`}
						placeholder={Constants.RECEIPT_DATE_TIME_PLACEHOLDER}
						defaultDate={claimDetails?.receipt_datetime}
						onDateChange={handleDateChange}
						onDateOk={handleDateOk}
					/>
				</Form.Item>
				<div className="md:flex">
					<Form.Item
						extra={fieldErrors?.purchase_amount && <ErrorMessage classes={styles?.err_message} message={Constants.ERROR_MESSAGE.PURCHASE_AMOUNT} />}
						name="purchase_amount"
						initialValue={claimDetails?.purchase_amount}
						label={<Label label={Constants.PURCHASE_AMOUNT} classes={{ label: styles?.basic_input_label }} requiredMark />}
					>
						<Input
							pattern="[0-9]"
							min={0}
							data-testid={Constants.PURCHASE_AMOUNT}
							placeholder={Constants.PURCHASE_AMOUNT_PLACEHOLDER}
							className={`${styles?.basic_input_small} ${claimDetails?.purchase_amount ? fieldStyle(!!fieldErrors?.purchase_amount) : ""}`}
							value={claimDetails?.purchase_amount}
							onChange={handleInputChange}
							onBlur={() => {
								validateFields(false);
							}}
							prefix={<InputPrefix prefixText={Constants.AMOUNT_INPUT_PREFIX} />}
						/>
					</Form.Item>
					<Form.Item
						extra={fieldErrors?.net_amount && <ErrorMessage classes={styles?.err_message} message={Constants.ERROR_MESSAGE.NET_AMOUNT} />}
						name="net_amount"
						initialValue={claimDetails?.net_amount}
						label={<Label label={Constants.NET_AMOUNT} classes={{ label: styles?.basic_input_label }} requiredMark />}
					>
						<Input
							pattern="[0-9]"
							min={0}
							data-testid={Constants.NET_AMOUNT}
							placeholder={Constants.NET_AMOUNT_PLACEHOLDER}
							className={`${styles?.basic_input_small} ${claimDetails?.net_amount ? fieldStyle(!!fieldErrors?.net_amount) : ""}`}
							value={claimDetails?.net_amount}
							onChange={handleInputChange}
							onBlur={() => {
								validateFields(false);
							}}
							prefix={<InputPrefix prefixText={Constants.AMOUNT_INPUT_PREFIX} />}
						/>
					</Form.Item>
				</div>
			</div>
		</Form>
	);
};

export default CreateClaimDetails;
